import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, DestroyRef,
  inject,
  input,
  linkedSignal, OnChanges, SimpleChanges, signal,
} from '@angular/core';
import {
  BudgetModelFormGroup,
} from "../../../pojo/bm-form";
import { TooltipComponent } from "../../../../../shared/component/tooltip/tooltip.component";
import { ToolTipSettings } from "../../../../../shared/component/tooltip/tooltip.util";
import { BmPeriodEnum } from "../../../pojo/bm-classes";
import { AllIconsComponent } from "../../../../../shared/component/all-icons/all-icons.component";
import { ButtonComponent } from "../../../../../shared/component/buttons/button.component";
import { tap } from "rxjs";
import { debounceTime, startWith } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { mergeWith } from "lodash";

@Component({
  selector: '[app-table-budget-model-headers]',
  imports: [
    AllIconsComponent,
    ButtonComponent,
    TooltipComponent,
  ],
  templateUrl: './table-budget-model-headers.component.html',
  styleUrl: '../../table-budget-model.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableBudgetModelHeadersComponent implements OnChanges {
  private cdr = inject(ChangeDetectorRef);

  protected readonly BmPeriodEnum = BmPeriodEnum;
  budgetForm = input.required<BudgetModelFormGroup>();
  collapsedYears = input.required<Map<number, boolean>>();
  budgetFormValue$$ = linkedSignal(() => {
    this.collapsedYears();
    return this.budgetForm().getRawValue();
  });

  expandTooltip$$ = signal<ToolTipSettings>({ show: false });

  destroyRef = inject(DestroyRef);

  showExpandTooltip(id: string, tooltipText: string): void {
    this.expandTooltip$$.set({
      show: true,
      coordinates: document.getElementById(id).getBoundingClientRect(),
      title: tooltipText,
    });
  }

  hideExpandTooltip(): void {
    this.expandTooltip$$.set({ show: false });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.budgetForm) {
      this.subscribeOnFormChanges();
    }
  }

  subscribeOnFormChanges(): void {
    this.budgetForm().controls.yearsNumber.valueChanges.pipe(
      startWith(-1),
      debounceTime(5), // form updates multiple times. We want to asap but leave an interval for batch updates.
      tap((yearsNumber) => {
        const isCopyEnabled = yearsNumber === -1 || yearsNumber === this.budgetFormValue$$().yearsNumber;
        this.updateLocalFormValue(isCopyEnabled);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  toggleAllGroups(): void {
    const modelDataFg = this.budgetForm().controls.modelData.controls;
    const modelData = this.budgetForm().getRawValue().modelData;
    // First click -> close all mgmFee groups.
    // Second click ->  If all mgmFee groups are closed => close all sections.
    if (modelData.mgmFee.groups.every((mgmGroup) => mgmGroup.showGroup === false)) {
      modelDataFg.mgmFee.controls.showSection.patchValue(false, { emitEvent: true });
    } else {
      modelDataFg.mgmFee.controls.groups.controls.forEach((mgmGroup) => mgmGroup.controls.showGroup.patchValue(false, { emitEvent: true }));
      modelDataFg.operatingExpenses.controls.showSection.patchValue(false, { emitEvent: true });
      modelDataFg.organizationalExpenses.controls.showSection.patchValue(false, { emitEvent: true });
      modelDataFg.investment.controls.showSection.patchValue(false, { emitEvent: true });
    }
    // Third click ->  If all sections are closed => open all sections and groups.
    if (!modelData.mgmFee.showSection && !modelData.operatingExpenses.showSection && !modelData.organizationalExpenses.showSection && !modelData.investment.showSection) {
      modelDataFg.mgmFee.controls.showSection.patchValue(true, { emitEvent: true });
      modelDataFg.mgmFee.controls.groups.controls.forEach((mgmGroup) => mgmGroup.controls.showGroup.patchValue(true, { emitEvent: true }));
      modelDataFg.operatingExpenses.controls.showSection.patchValue(true, { emitEvent: true });
      modelDataFg.organizationalExpenses.controls.showSection.patchValue(true, { emitEvent: true });
      modelDataFg.investment.controls.showSection.patchValue(true, { emitEvent: true });
    }
  }

  toggleAllQuarters(): void {
    const updatedValue = !this.budgetForm().getRawValue().isAllQuartersCollapsed;
    this.budgetForm().controls.isAllQuartersCollapsed.patchValue(updatedValue, { emitEvent: true });

    const years = Number(this.budgetForm().controls.yearsNumber.value) || 0;

    /* update every QUARTER cell; year-cells get the same flag if you wish */
    this.budgetForm().controls.modelHeaders.controls.forEach((header) =>
      header.controls.isCollapsed.patchValue(updatedValue, { emitEvent: true }),
    );

    /* sync the helper array */
    const newArray = updatedValue ? Array.from({ length: years }, (_, i) => i + 1) : [];
    this.budgetForm().controls.collapsedYears.patchValue(newArray, { emitEvent: true });
    this.updateLocalFormValue(true);
  }

  toggleQuartersInSelectedYear(yearIdx: number): void {
    const shouldCollapse = !this.budgetForm().getRawValue().collapsedYears.includes(yearIdx);

    this.budgetForm().controls.modelHeaders.controls
      .filter((header) => header.value.yearNumber === yearIdx)
      .forEach((header) => header.controls.isCollapsed.patchValue(!header.controls.isCollapsed.value, { emitEvent: true }));

    const collapsedYears: number[] = this.budgetForm().controls.collapsedYears.value;
    const updatedCollapsedYears = shouldCollapse ? Array.from(new Set([...collapsedYears, yearIdx])) : collapsedYears.filter((y) => y !== yearIdx);
    this.budgetForm().controls.collapsedYears.patchValue(updatedCollapsedYears, { emitEvent: true });
    this.updateLocalFormValue(true);
  }

  private updateLocalFormValue(isCopyEnabled: boolean): void {
    const newRawValue = this.budgetForm().getRawValue();
    if (isCopyEnabled) {
      mergeWith(this.budgetFormValue$$(), newRawValue);
    } else {
      this.budgetFormValue$$.set(newRawValue);
    }
    this.cdr.detectChanges();
  }
}
